import { useCallback, useEffect, useRef, useState } from "react";
import Page from "./Page";
import Node from "../components/pages/organization/Node";
import Connection from "../components/pages/organization/Connection";
import { useDispatch, useSelector } from "react-redux";
import { routeGroupActions } from "../store/slices/routeGroupSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { connectionActions } from "../store/slices/connectionSlice";
import { ArrowBack } from "@mui/icons-material";
import { Box } from "@mui/material";
import { Button } from "bootstrap";

const Organization = () => {
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [connectingNodeId, setConnectingNodeId] = useState(null);
  const dispatch = useDispatch();
  const { routeGroups } = useSelector((state) => state.routeGroup);
  const { connections: sagaConnections } = useSelector(
    (state) => state.connection
  );
  const location = useLocation();
  const moveTimeoutRef = useRef(null);
  const [date, setDate] = useState(null);
  const { routeGroupsForCopy } = useSelector((state) => state.routeGroup);

  const fetchRouteGroupsAndConnections = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    const qDate = queryParams.get("date");

    setDate(qDate);

    dispatch(routeGroupActions.getRouteGroupsFetch({ date: qDate }));
    dispatch(connectionActions.getConnectionsFetch({ date: qDate }));
  }, [dispatch, location]);

  useEffect(() => {
    fetchRouteGroupsAndConnections();
  }, [fetchRouteGroupsAndConnections]);

  const onSucceededCreatedConnection = useCallback(() => {
    dispatch(connectionActions.getConnectionsFetch({ date }));
  }, [date, dispatch]);

  useEffect(() => {
    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    };

    if (routeGroupsForCopy && routeGroupsForCopy.length > 0) {
      copyToClipboard(generateFormattedText(routeGroupsForCopy));
    }
  }, [routeGroupsForCopy]);

  function generateFormattedText(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return "Nema dostupnih podataka za kopiranje.";
    }

    return data
      .map((group) => {
        try {
          const { title, routes = [], externalArrival = [] } = group || {};

          let output = `Naslov: ${title || "N/A"}\n`;

          const route = routes.length > 0 ? routes[0] : null;

          if (!route) {
            output += "Nema informacija o ruti.\n";
            return output + "\n----------------------------------------\n";
          }

          output += `Kontakt telefon: ${route.contactPhone || "/"}\n`;
          output += `Broj odraslih putnika: ${
            route.numberOfAdultPassengers ?? "N/A"
          }\n`;

          if (route.largeSeat > 0)
            output += `Veliko sedište: ${route.largeSeat}\n`;
          if (route.mediumSeat > 0)
            output += `Srednje sedište: ${route.mediumSeat}\n`;
          if (route.smallSeat > 0)
            output += `Malo sedište: ${route.smallSeat}\n`;
          if (route.address) output += `Adresa: ${route.address}\n`;

          // Putnici
          output += `\nPutnici:\n`;
          if (Array.isArray(route.passengers) && route.passengers.length > 0) {
            route.passengers.forEach((p, idx) => {
              output += `- ${idx + 1}. Ime i prezime: ${
                p.fullName || "Nepoznato"
              }\n`;
              output += `  Adresa: ${p.address || "/"}\n`;
              output += `  Kontakt: ${p.contactPhoneNumber || "/"}\n`;
            });
          } else {
            output += "Nema podataka o putnicima.\n";
          }

          // Dovodi
          output += `\nDovodi:\n`;
          if (Array.isArray(externalArrival) && externalArrival.length > 0) {
            externalArrival.forEach((ext) => {
              output += `- ${ext.vehicle || "Nepoznato vozilo"} (vozi: ${
                ext.driver || "Nepoznat vozač"
              }) - ${ext.numberOfPassengers || 0} putnik\n`;
            });
          } else {
            output += `Nema eksternih dovoda.\n`;
          }

          // Ukupan broj putnika
          const internalCount = route.passengers?.length || 0;
          const externalCount = externalArrival.reduce(
            (sum, x) => sum + (x.numberOfPassengers || 0),
            0
          );
          const totalPassengers = internalCount + externalCount;

          output += `\nUkupno putnika: ${totalPassengers}`;
          if (externalCount > 0) output += ` (${externalCount} dovodi)`;

          return output + `\n\n----------------------------------------\n`;
        } catch (err) {
          console.error("❌ Greška pri obradi grupe:", err);
          return `⚠️ Greška pri obradi jedne grupe podataka.\n----------------------------------------\n`;
        }
      })
      .join("");
  }

  useEffect(() => {
    if (routeGroups.length > 0) {
      setNodes(
        routeGroups.map((routeGroup) => ({
          id: routeGroup.id,
          title: routeGroup.title,
          color: routeGroup.color,
          routes: routeGroup.routes,
          x:
            routeGroup.xAxis !== 0
              ? routeGroup.xAxis
              : Math.random() * 400 + 64,
          y:
            routeGroup.yAxis !== 0
              ? routeGroup.yAxis
              : Math.random() * 300 + 64,
        }))
      );
    }
  }, [routeGroups]);

  useEffect(() => {
    if (sagaConnections && sagaConnections.length > 0) {
      setConnections(
        sagaConnections.map((sc) => ({
          id: sc.id,
          fromId: sc.fromId,
          toId: sc.toId,
          vehicle: sc.vehicle,
          driver: sc.driver,
        }))
      );
    }
  }, [sagaConnections]);

  const moveNode = useCallback(
    (id, x, y) => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }

      setNodes((prev) =>
        prev.map((node) => (node.id === id ? { ...node, x, y } : node))
      );

      moveTimeoutRef.current = setTimeout(() => {
        moveTimeoutRef.current = null;
        dispatch(
          routeGroupActions.updateRouteGroupPut({
            id: id,
            payload: { xAxis: x, yAxis: y, title: null, routes: null },
          })
        );
      }, 1500);
    },
    [dispatch]
  );

  const selectNode = useCallback((id) => {
    setSelectedNodeId(id);
  }, []);

  const handleNodeDoubleClick = useCallback(
    (id) => {
      if (connectingNodeId === null) {
        setConnectingNodeId(id);
      } else if (connectingNodeId === id) {
        setConnectingNodeId(null);
      } else {
        const connectionExists = connections.some(
          (conn) =>
            (conn.fromId === connectingNodeId && conn.toId === id) ||
            (conn.fromId === id && conn.toId === connectingNodeId)
        );

        if (connectionExists) {
        } else {
          const newConnection = {
            id: `${connectingNodeId}-${id}`,
            fromId: connectingNodeId,
            toId: id,
          };

          dispatch(
            connectionActions.createConnectionPost({
              obj: { title: "00-01", fromId: connectingNodeId, toId: id, date },
              onSucceeded: onSucceededCreatedConnection,
            })
          );

          setConnections((prev) => [...prev, newConnection]);
        }
        setConnectingNodeId(null);
      }
    },
    [
      connectingNodeId,
      connections,
      dispatch,
      date,
      onSucceededCreatedConnection,
    ]
  );

  const deleteConnection = useCallback(
    (connectionId) => {
      setConnections((prev) => prev.filter((conn) => conn.id !== connectionId));
      dispatch(connectionActions.deleteConnectionDelete({ id: connectionId }));
    },
    [dispatch]
  );

  const copyRouteGroupsHandler = () => {
    dispatch(routeGroupActions.getForCopyRouteGroupsFetch({ date }));
  };

  const navigate = useNavigate();
  return (
    <Page
      classes="h-screen"
      sx={{
        position: "relative",
      }}
    >
      <div className="w-full flex flex-col" style={{ height: "100vh" }}>
        {/* <Toolbar
          onAddNode={addNode}
          onClearAll={clearAll}
          onClearConnections={clearConnections}
          nodeCount={nodes.length}
          connectionCount={connections.length}
        /> */}

        <div
          className="flex-1 relative bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-lg overflow-auto"
          style={{
            minHeight: "500px",
            height:
              nodes.length > 0
                ? Math.max(...nodes.map((node) => node.y)) + 400
                : 500,
          }}
        >
          {/* Grid pattern background */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
              linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
            `,
              backgroundSize: "20px 20px",
            }}
          />

          {connections.map((connection) => {
            const fromNode = nodes.find(
              (node) => node.id === connection.fromId
            );
            const toNode = nodes.find((node) => node.id === connection.toId);

            if (!fromNode || !toNode) return null;

            return (
              <Connection
                key={connection.id}
                id={connection.id}
                from={{ x: fromNode.x, y: fromNode.y }}
                to={{ x: toNode.x, y: toNode.y }}
                vehicle={connection.vehicle}
                driver={connection.driver}
                onDelete={deleteConnection}
              />
            );
          })}

          {nodes.map((node) => (
            <Node
              key={node.id}
              id={node.id}
              x={node.x}
              y={node.y}
              title={node.title}
              color={node.color}
              isSelected={
                selectedNodeId === node.id || connectingNodeId === node.id
              }
              date={date}
              routes={node.routes}
              onMove={moveNode}
              onSelect={selectNode}
              onDoubleClick={handleNodeDoubleClick}
              fetchRouteGroupsAndConnections={fetchRouteGroupsAndConnections}
            />
          ))}
        </div>
      </div>
      {/* <button onClick={copyRouteGroupsHandler}>Copy</button> */}
      <Box
        sx={{
          position: "absolute",
          top: "10px",
          left: "10px",
          cursor: "pointer",
        }}
        onClick={() => navigate(-1)}
      >
        <ArrowBack fontSize="large" />
      </Box>
    </Page>
  );
};

export default Organization;
